import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import * as api from '../api/contentStudioV2';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import StatusBadge from '../components/common/StatusBadge';

const MySubmissions = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const t = useLang();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  
  // Video submission state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoNotes, setVideoNotes] = useState('');
  const [videoSubmitting, setVideoSubmitting] = useState(false);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getMySubmissions(token, user.user_id);
      setSubmissions(response.submissions || []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [token, user?.user_id]);

  useEffect(() => {
    if (user?.user_id) {
      loadSubmissions();
    }
  }, [user?.user_id, loadSubmissions]);

  const handleViewStoryboard = async (sessionId) => {
    try {
      const sessionData = await api.getSessionData(token, sessionId);
      setSelectedSubmission(sessionData);
    } catch (error) {
      console.error('Failed to load session:', error);
      alert('Failed to load storyboard. Please try again.');
    }
  };

  // Video submission handlers
  const handleOpenVideoModal = (submission) => {
    setSelectedSubmission(submission);
    setVideoUrl(submission.video_url || '');
    setVideoNotes(submission.video_notes || '');
    setShowVideoModal(true);
  };

  const handleSubmitVideo = async () => {
    if (!videoUrl.trim()) {
      alert('Please enter a video URL');
      return;
    }
    
    // Basic URL validation
    if (!videoUrl.startsWith('http://') && !videoUrl.startsWith('https://')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }

    try {
      setVideoSubmitting(true);
      await api.submitVideo(token, selectedSubmission.session_id, videoUrl, videoNotes);
      alert('✅ Video submitted for review!');
      setShowVideoModal(false);
      setVideoUrl('');
      setVideoNotes('');
      loadSubmissions(); // Reload to see updated status
    } catch (error) {
      console.error('Failed to submit video:', error);
      alert('Failed to submit video: ' + error.message);
    } finally {
      setVideoSubmitting(false);
    }
  };

  // Resubmit for approval (changes status back to pending_approval)
  const handleResubmit = async (sessionId) => {
    if (!confirm('Are you sure you want to resubmit this for approval?')) return;
    
    try {
      await api.resubmitForApproval(token, sessionId);
      alert('✅ Resubmitted for approval!');
      loadSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      console.error('Failed to resubmit:', error);
      alert('Failed to resubmit: ' + error.message);
    }
  };

  const getVideoStatusBadge = (status) => {
    if (!status) return null;
    const mapped = status === 'approved' ? 'video_approved' : status === 'rejected' ? 'video_rejected' : status;
    return <StatusBadge status={mapped} size="sm" />;
  };

  const generatePDF = () => {
    if (!selectedSubmission) return;

    setDownloadLoading(true);
    
    // Build the storyboard URL that the QR code will link to
    const baseUrl = window.location.origin;
    const storyboardUrl = `${baseUrl}/queue/review/${selectedSubmission.session_id}`;
    
    // Build PDF content using the browser's print functionality
    const content = buildPDFContent(selectedSubmission, storyboardUrl);
    
    // Open in new window for printing/saving as PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    
    // Give it a moment to render before printing
    setTimeout(() => {
      printWindow.print();
      setDownloadLoading(false);
    }, 500);
  };

  const buildPDFContent = (session, qrCodeUrl) => {
    const storyboardBeats = session.storyboard_beats || [];
    const platformContext = session.platform_context || {};
    const selectedFormat = session.selected_format || {};
    const selectedScript = session.selected_script || {};
    
    // Build script text
    let scriptText = selectedScript.script_text || '';
    if (!scriptText && selectedScript.sections) {
      scriptText = selectedScript.sections.map(s => 
        `[${s.title}] (${s.duration_seconds}s)\n${s.lines?.join('\n') || s.content || ''}`
      ).join('\n\n');
    }

    return `
<!DOCTYPE html>
<html>
<head>
  <title>Storyboard - ${session.objective_type || 'Content'} - ${session.submitted_at ? new Date(session.submitted_at).toLocaleDateString('ms-MY') : 'Draft'}</title>
  <style>
    body { 
      font-family: Arial, sans-serif; 
      padding: 20px; 
      max-width: 800px; 
      margin: 0 auto;
      color: #333;
    }
    .header { 
      text-align: center; 
      border-bottom: 3px solid #0e67a2; 
      padding-bottom: 20px; 
      margin-bottom: 20px;
    }
    .header h1 { 
      color: #0e67a2; 
      margin-bottom: 5px;
      font-size: 24px;
    }
    .header .subtitle { 
      color: #666; 
      font-size: 12px;
    }
    .status-badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: bold;
      margin-top: 10px;
    }
    .approved { background: #4CAF50; color: white; }
    .pending { background: #FF9800; color: white; }
    .rejected { background: #F44336; color: white; }
    .meta-grid { 
      display: grid; 
      grid-template-columns: repeat(3, 1fr); 
      gap: 15px; 
      margin-bottom: 25px;
      background: #f5f5f5;
      padding: 15px;
      border-radius: 8px;
    }
    .meta-item { font-size: 12px; }
    .meta-label { color: #666; font-weight: normal; }
    .meta-value { font-weight: bold; color: #333; }
    .section { margin-bottom: 25px; }
    .section-title { 
      color: #0e67a2; 
      border-bottom: 2px solid #eee; 
      padding-bottom: 8px;
      margin-bottom: 15px;
      font-size: 16px;
    }
    .beat { 
      background: #f9f9f9; 
      padding: 15px; 
      margin-bottom: 12px; 
      border-radius: 8px;
      border-left: 4px solid #0e67a2;
      page-break-inside: avoid;
    }
    .beat-header { 
      display: flex; 
      align-items: center; 
      gap: 10px; 
      margin-bottom: 10px;
    }
    .beat-number { 
      background: #0e67a2; 
      color: white; 
      padding: 2px 8px; 
      border-radius: 4px;
      font-size: 12px;
      font-weight: bold;
    }
    .timing { color: #666; font-size: 11px; }
    .scene-type { 
      font-size: 10px; 
      padding: 2px 6px; 
      border-radius: 3px; 
      border: 1px solid #ccc;
    }
    .description { font-size: 13px; margin-bottom: 10px; }
    .notes-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .note-box { 
      background: white; 
      padding: 8px; 
      border-radius: 4px; 
      font-size: 11px;
    }
    .note-label { font-weight: bold; margin-bottom: 4px; }
    .visual-box { border-left: 3px solid #2196F3; }
    .audio-box { border-left: 3px solid #9C27B0; }
    .script-box {
      background: #f5f5f5;
      padding: 15px;
      border-radius: 8px;
      white-space: pre-wrap;
      font-size: 12px;
      page-break-inside: avoid;
    }
    .footer { 
      margin-top: 30px; 
      padding-top: 15px; 
      border-top: 1px solid #ddd; 
      font-size: 10px; 
      color: #666;
      text-align: center;
    }
    .qr-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin: 20px auto;
      padding: 15px;
      background: #f9f9f9;
      border-radius: 8px;
      max-width: 200px;
    }
    .qr-code {
      width: 120px;
      height: 120px;
      margin-bottom: 8px;
    }
    .qr-label {
      font-size: 10px;
      color: #666;
      text-align: center;
    }
    .qr-url {
      font-size: 8px;
      color: #999;
      word-break: break-all;
      max-width: 180px;
      text-align: center;
      margin-top: 5px;
    }
    @media print {
      body { padding: 10px; }
      .beat { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>🎬 Production Storyboard</h1>
    <div class="subtitle">${session.submission_id || session.session_id}</div>
    <div class="status-badge ${session.submission_status === 'approved' ? 'approved' : session.submission_status === 'rejected' ? 'rejected' : 'pending'}">
      ${session.submission_status?.toUpperCase() || 'PENDING'}
    </div>
  </div>

  <div class="meta-grid">
    <div class="meta-item">
      <div class="meta-label">Objective</div>
      <div class="meta-value">${session.objective_type || 'N/A'}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Platform</div>
      <div class="meta-value">${platformContext.channels?.join(', ') || 'N/A'}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Duration</div>
      <div class="meta-value">${platformContext.normalized_seconds || 60}s</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Format</div>
      <div class="meta-value">${selectedFormat.format_name || 'N/A'}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Submitted</div>
      <div class="meta-value">${session.submitted_at ? new Date(session.submitted_at).toLocaleDateString() : 'N/A'}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Approved</div>
      <div class="meta-value">${session.decided_at ? new Date(session.decided_at).toLocaleDateString() : 'Pending'}</div>
    </div>
  </div>

  ${session.supervisor_comment ? `
  <div class="section">
    <h3 class="section-title">📝 Supervisor Notes</h3>
    <div style="background: #e3f2fd; padding: 12px; border-radius: 8px; font-size: 13px;">
      ${session.supervisor_comment}
    </div>
  </div>
  ` : ''}

  <div class="section">
    <h3 class="section-title">📝 Script (Variant ${selectedScript.variant_id || 'A'})</h3>
    <div class="script-box">${scriptText || 'No script available'}</div>
  </div>

  <div class="section">
    <h3 class="section-title">🎬 Storyboard Beats (${storyboardBeats.length} scenes)</h3>
    ${storyboardBeats.map((beat, i) => `
      <div class="beat">
        <div class="beat-header">
          <span class="beat-number">Beat ${beat.beat_number || i + 1}</span>
          <span class="timing">${beat.timing}</span>
          <span class="scene-type">${beat.scene_type}</span>
          <span class="timing">${beat.duration_seconds}s</span>
        </div>
        <div class="description">${beat.description}</div>
        <div class="notes-grid">
          <div class="note-box visual-box">
            <div class="note-label">👁️ Visual</div>
            <div>${beat.visual_notes || 'N/A'}</div>
          </div>
          <div class="note-box audio-box">
            <div class="note-label">🔊 Audio</div>
            <div>${beat.audio_notes || 'N/A'}</div>
          </div>
        </div>
      </div>
    `).join('')}
  </div>

  <div class="footer">
    <div class="qr-section">
      <img class="qr-code" src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(qrCodeUrl)}" alt="QR Code" />
      <div class="qr-label">📱 Scan to view digital storyboard</div>
      <div class="qr-url">${qrCodeUrl}</div>
    </div>
    <div>Generated by EMZI NEXUS Content Studio</div>
    <div>Session: ${session.session_id} | ${new Date().toLocaleString()}</div>
  </div>
</body>
</html>
    `;
  };

  const getStatusBadge = (status) => {
    return <StatusBadge status={status} size="sm" />;
  };

  if (loading) {
    return (
      <div className="nexus-content bg-white flex items-center justify-center">
        <div className="text-gray-600 font-medium">Loading submissions...</div>
      </div>
    );
  }

  return (
    <div className="nexus-content overflow-hidden bg-gray-50 flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button 
              onClick={() => navigate('/dashboard')} 
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white hover:bg-opacity-20"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-black text-white">My Submissions</h1>
              <p className="text-sm text-white opacity-80">
                {submissions.length} total submissions
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 py-4 pb-28">
        {selectedSubmission ? (
          // Storyboard Detail View
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Button onClick={() => setSelectedSubmission(null)} variant="outline">
                ← Back to List
              </Button>
              <Button 
                onClick={generatePDF} 
                disabled={downloadLoading}
                className="bg-emzi-blue hover:bg-blue-700 text-white"
              >
                {downloadLoading ? '⏳ Generating...' : '📄 Download PDF'}
              </Button>
            </div>

            {/* Status */}
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-lg font-bold">{selectedSubmission.project_name || `${selectedSubmission.objective_type || 'Storyboard'} - ${selectedSubmission.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'Draft'}`}</div>
                    <div className="text-sm text-gray-500">
                      📅 {new Date(selectedSubmission.submitted_at).toLocaleString('ms-MY')}
                    </div>
                  </div>
                  {getStatusBadge(selectedSubmission.submission_status)}
                </div>
                {selectedSubmission.supervisor_comment && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm font-bold text-blue-800">📝 Supervisor Feedback:</div>
                    <div className="text-sm text-blue-700">{selectedSubmission.supervisor_comment}</div>
                  </div>
                )}
                
                {/* Edit & Resubmit Section - Only for changes_requested status */}
                {selectedSubmission.submission_status === 'changes_requested' && (
                  <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="text-sm font-bold text-orange-800 mb-2">✏️ Action Required</div>
                    <p className="text-sm text-orange-700 mb-3">
                      Please make the requested changes and resubmit for approval.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        onClick={() => navigate(`/studio/content-v2?session=${selectedSubmission.session_id}&step=8`)}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        🎯 Edit Format
                      </Button>
                      <Button 
                        onClick={() => navigate(`/studio/content-v2?session=${selectedSubmission.session_id}&step=9`)}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        📝 Edit Script
                      </Button>
                      <Button 
                        onClick={() => navigate(`/studio/content-v2?session=${selectedSubmission.session_id}&step=10`)}
                        className="bg-purple-500 hover:bg-purple-600 text-white"
                      >
                        🎬 Edit Storyboard
                      </Button>
                      <Button 
                        onClick={() => handleResubmit(selectedSubmission.session_id)}
                        variant="outline"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                      >
                        🔄 Resubmit As Is
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Storyboard Beats */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold">
                  🎬 Storyboard ({selectedSubmission.storyboard_beats?.length || 0} beats)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {(selectedSubmission.storyboard_beats || []).map((beat, index) => (
                    <div key={`item-${index}`} className="bg-gray-50 rounded-lg p-4 border-l-4 border-emzi-blue">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-emzi-blue text-white">Beat {beat.beat_number || index + 1}</Badge>
                        <span className="text-sm text-gray-600">{beat.timing}</span>
                        <Badge variant="outline">{beat.scene_type}</Badge>
                        <span className="text-xs text-gray-500">{beat.duration_seconds}s</span>
                      </div>
                      <div className="text-sm font-medium text-gray-800 mb-2">{beat.description}</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-blue-50 rounded p-2">
                          <span className="font-bold text-blue-700">👁️ Visual:</span>
                          <div className="text-gray-700">{beat.visual_notes}</div>
                        </div>
                        <div className="bg-purple-50 rounded p-2">
                          <span className="font-bold text-purple-700">🔊 Audio:</span>
                          <div className="text-gray-700">{beat.audio_notes}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Submissions List
          <div className="space-y-3">
            {submissions.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="py-12 text-center">
                  <div className="text-6xl mb-4">📭</div>
                  <h3 className="text-xl font-bold text-gray-800">No Submissions Yet</h3>
                  <p className="text-gray-600 mt-2">Create content in Content Studio to see your submissions here.</p>
                  <Button onClick={() => navigate('/studios')} className="mt-4 bg-emzi-blue">
                    Go to Studios
                  </Button>
                </CardContent>
              </Card>
            ) : (
              submissions.map((sub) => (
                <Card key={sub.session_id} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 cursor-pointer" onClick={() => handleViewStoryboard(sub.session_id)}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-gray-800">{sub.project_name || `${sub.objective_type || 'Storyboard'} - ${sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString('ms-MY', { day: '2-digit', month: 'short' }) : 'Draft'}`}</span>
                          {getStatusBadge(sub.submission_status)}
                          {sub.video_status && getVideoStatusBadge(sub.video_status)}
                        </div>
                        <div className="text-sm text-gray-600">
                          🎬 {sub.format_name || 'Unknown Format'} • {sub.storyboard_beats_count || 0} beats
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          📅 {new Date(sub.submitted_at).toLocaleString('ms-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {sub.video_url && (
                          <div className="mt-2 text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded inline-block">
                            🎬 <a href={sub.video_url} target="_blank" rel="noopener noreferrer" className="underline" onClick={(e) => e.stopPropagation()}>View Video</a>
                          </div>
                        )}
                        {sub.supervisor_comment && (
                          <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            💬 {sub.supervisor_comment}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Button variant="ghost" size="sm" className="text-emzi-blue" onClick={() => handleViewStoryboard(sub.session_id)}>
                          View →
                        </Button>
                        {/* Show Submit Video button for approved storyboards that don't have video yet */}
                        {sub.submission_status === 'approved' && !sub.video_status && (
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenVideoModal(sub);
                            }}
                          >
                            📹 Submit Video
                          </Button>
                        )}
                        {/* Show Re-submit button if video was rejected */}
                        {sub.video_status === 'rejected' && (
                          <Button 
                            size="sm" 
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenVideoModal(sub);
                            }}
                          >
                            🔄 Re-submit Video
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Video Submission Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-lg">📹 Submit Final Video</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                <p className="font-medium">📋 Your storyboard has been approved!</p>
                <p className="mt-1">Submit your final video link (YouTube, TikTok, Telegram, etc.) for supervisor review.</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL *
                </label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://youtube.com/watch?v=... or https://t.me/..."
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported: YouTube, TikTok, Telegram, Vimeo, or any public link
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <Textarea
                  value={videoNotes}
                  onChange={(e) => setVideoNotes(e.target.value)}
                  placeholder="Any notes for the supervisor..."
                  rows={3}
                  className="w-full"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowVideoModal(false);
                    setVideoUrl('');
                    setVideoNotes('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleSubmitVideo}
                  disabled={videoSubmitting || !videoUrl.trim()}
                >
                  {videoSubmitting ? '⏳ Submitting...' : '📤 Submit Video'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MySubmissions;
